import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { highlightJSON } from '../utils/highlightJSON';
const pageData = {
  $schema: 'https://schema.org/WebPage',
  name: 'Andoe / Switch to Andoe',
  frontmatter: {
    page: 'Switch',
    topic: 'Migration',
    status: 'Active'
  },
  hero: {
    kicker: 'Andoe / Switch',
    headline: 'The way businesses are discovered is changing.',
    lede: 'AI systems now act as an intermediary between businesses and customers. People increasingly rely on AI to understand products, compare options, and make decisions.'
  },
  sections: [{
    ref: '01',
    title: 'The Industry',
    deck: "What's happening in search right now.",
    entries: [{
      ref: '1.1',
      label: 'Shift',
      headline: 'Discovery is moving upstream',
      body: "AI answers questions before people visit a website. If your business doesn't appear clearly in those responses, it may never enter the consideration set."
    }, {
      ref: '1.2',
      label: 'Metrics',
      headline: 'Visibility is no longer just traffic',
      body: "Ranking and publishing more content aren't enough. What matters is how consistently your business is described across prompts, tools, and contexts."
    }, {
      ref: '1.3',
      label: 'Tooling',
      headline: "Existing tools don't show this layer",
      body: "SEO tools track keywords and backlinks. Analytics tools track visits and conversions. They don't show how AI systems understand and describe your business."
    }]
  }, {
    ref: '02',
    title: 'The Problem',
    deck: 'More is not always better.',
    entries: [{
      observation: 'The AI visibility space is evolving quickly with agency-style, SEO-extension, and full-automation approaches.',
      gap: "What's missing is a simpler way to see how AI understands a business and what actions actually matter."
    }, {
      headline: 'Built with product marketers, not around them',
      questions: ['Which prompts actually shape discovery?', "Which ones aren't worth optimizing?", "Where are competitors showing up that you're not?", 'What should you write next, and why?']
    }]
  }, {
    ref: '03',
    title: 'The Switch',
    deck: 'Who switches and how it works.',
    entries: [{
      headline: 'Action over tracking',
      body: "Teams switch to Andoe when they realize a dashboard is not a strategy. Most AI visibility tools cater to the trackers: those who want to watch every rank change, prompt variation, and shifting metric. This creates more charts to manage and more data to export, but it results in more noise than progress. Tracking is a passive act. The end goal of AI visibility is not to build a library of tracked prompts. It is to identify the high-leverage actions that move the needle. You do not need to see your name in a list. You need to know exactly what to publish next to bridge a visibility gap. At Andoe, we prioritize strategic movement over static monitoring. Instead of obsessing over a single prompt's performance, we help you execute the content shifts that improve your visibility across entire clusters of queries. We do not just tell you where you are. We tell you what to do to get where you are going."
    }]
  }],
  end_cta: {
    label: 'Next',
    headline: 'Ready to switch?',
    body: 'Join the teams moving from noise to clarity.',
    cta: {
      label: 'Contact us',
      href: '/contact'
    }
  }
};
export function SwitchPage() {
  const [jsonHtml, setJsonHtml] = useState('');
  useEffect(() => {
    setJsonHtml('<span class="c">// the case for switching, as structured data</span>\n\n' + highlightJSON(pageData));
  }, []);
  return <>
      <Navigation data-id="element-394" />

      <main className="wrap report-main" data-id="element-395">
        <article className="report" data-id="element-396">
          {/* frontmatter */}
          <header className="frontmatter" data-id="element-397">
            <div className="fm-cell" data-id="element-398">
              <span className="lbl" data-id="element-399">Page</span>
              <span className="val" data-id="element-400">Switch</span>
            </div>
            <div className="fm-cell" data-id="element-401">
              <span className="lbl" data-id="element-402">Topic</span>
              <span className="val" data-id="element-403">Migration</span>
            </div>
            <div className="fm-cell" data-id="element-404">
              <span className="lbl" data-id="element-405">Status</span>
              <span className="val" data-id="element-406">Active</span>
            </div>
          </header>

          {/* HERO */}
          <section className="hero" data-id="element-407">
            <div className="kicker" data-id="element-408">Andoe / Switch</div>
            <h1 data-id="element-409">The way businesses are discovered is changing.</h1>
            <p className="lede" data-id="element-410">
              AI systems now act as an intermediary between businesses and
              customers. People increasingly rely on AI to understand products,
              compare options, and make decisions.
            </p>
          </section>

          {/* SECTION 01 */}
          <section className="report-section" data-id="element-411">
            <div className="section-head" data-id="element-412">
              <div className="section-num" data-id="element-413">01</div>
              <div className="section-title" data-id="element-414">
                <h2 data-id="element-415">The Industry</h2>
                <span className="deck" data-id="element-416">
                  What's happening in search right now.
                </span>
              </div>
            </div>
            <div className="section-body" data-id="element-417">
              <div className="pad" data-id="element-418"></div>
              <div className="content" data-id="element-419">
                <div className="entry" data-id="element-420">
                  <div className="entry-label" data-id="element-421">
                    <span className="num" data-id="element-422">1.1</span> Shift
                  </div>
                  <h3 data-id="element-423">Discovery is moving upstream</h3>
                  <p data-id="element-424">
                    AI answers questions before people visit a website. If your
                    business doesn't appear clearly in those responses, it may
                    never enter the consideration set.
                  </p>
                </div>
                <div className="entry" data-id="element-425">
                  <div className="entry-label" data-id="element-426">
                    <span className="num" data-id="element-427">1.2</span> Metrics
                  </div>
                  <h3 data-id="element-428">Visibility is no longer just traffic</h3>
                  <p data-id="element-429">
                    Ranking and publishing more content aren't enough. What
                    matters is how consistently your business is described
                    across prompts, tools, and contexts.
                  </p>
                </div>
                <div className="entry" data-id="element-430">
                  <div className="entry-label" data-id="element-431">
                    <span className="num" data-id="element-432">1.3</span> Tooling
                  </div>
                  <h3 data-id="element-433">Existing tools don't show this layer</h3>
                  <p data-id="element-434">
                    SEO tools track keywords and backlinks. Analytics tools
                    track visits and conversions. They don't show how AI systems
                    understand and describe your business.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 02 */}
          <section className="report-section" data-id="element-435">
            <div className="section-head" data-id="element-436">
              <div className="section-num" data-id="element-437">02</div>
              <div className="section-title" data-id="element-438">
                <h2 data-id="element-439">The Problem</h2>
                <span className="deck" data-id="element-440">More is not always better.</span>
              </div>
            </div>
            <div className="section-body" data-id="element-441">
              <div className="pad" data-id="element-442"></div>
              <div className="content" data-id="element-443">
                <div className="entry" data-id="element-444">
                  <p data-id="element-445">
                    The AI visibility space is evolving quickly. Some take a
                    human-in-the-loop, agency-style approach. Others extend
                    traditional SEO tooling into AI contexts. Some promise full
                    automation.
                  </p>
                  <p style={{
                  marginTop: '16px'
                }} data-id="element-446">
                    What's missing is a simpler way to see how AI understands a
                    business and what actions actually matter, without
                    unnecessary layers or jargon.
                  </p>
                </div>
                <div className="entry" data-id="element-447">
                  <h3 data-id="element-448">Built with product marketers, not around them</h3>
                  <p data-id="element-449">
                    Modern product marketing teams already know their customers.
                    They can write, position, launch, and execute using dozens
                    of capable tools. What they don't have is clarity on where
                    to focus.
                  </p>
                  <ul style={{
                  margin: '16px 0',
                  paddingLeft: '20px',
                  color: 'var(--ink-2)',
                  fontSize: '15px'
                }} data-id="element-450">
                    <li data-id="element-451">Which prompts actually shape discovery?</li>
                    <li data-id="element-452">Which ones aren't worth optimizing?</li>
                    <li data-id="element-453">Where are competitors showing up that you're not?</li>
                    <li data-id="element-454">What should you write next, and why?</li>
                  </ul>
                  <p data-id="element-455">
                    There's no new language to learn. Just clear prompt data,
                    competitive comparison, and recommendations that explain
                    what to act on and why.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* SECTION 03 */}
          <section className="report-section" data-id="element-456">
            <div className="section-head" data-id="element-457">
              <div className="section-num" data-id="element-458">03</div>
              <div className="section-title" data-id="element-459">
                <h2 data-id="element-460">The Switch</h2>
                <span className="deck" data-id="element-461">Who switches and how it works.</span>
              </div>
            </div>
            <div className="section-body" data-id="element-462">
              <div className="pad" data-id="element-463"></div>
              <div className="content" data-id="element-464">
                <div className="entry" data-id="element-465">
                  <h3 data-id="element-466">Action over tracking</h3>
                  <p data-id="element-467">
                    Teams switch to Andoe when they realize a dashboard is not a
                    strategy. Most AI visibility tools cater to the trackers:
                    those who want to watch every rank change, prompt variation,
                    and shifting metric. This creates more charts to manage and
                    more data to export, but it results in more noise than
                    progress.
                  </p>
                  <p style={{
                  marginTop: '16px'
                }} data-id="element-468">
                    Tracking is a passive act. The end goal of AI visibility is
                    not to build a library of tracked prompts. It is to identify
                    the high-leverage actions that move the needle. You do not
                    need to see your name in a list. You need to know exactly
                    what to publish next to bridge a visibility gap.
                  </p>
                  <p style={{
                  marginTop: '16px'
                }} data-id="element-469">
                    At Andoe, we prioritize strategic movement over static
                    monitoring. Instead of obsessing over a single prompt's
                    performance, we help you execute the content shifts that
                    improve your visibility across entire clusters of queries.
                    We do not just tell you where you are. We tell you what to
                    do to get where you are going.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* END CTA */}
          <section className="end-cta" id="start" data-id="element-470">
            <div className="end-label" data-id="element-471">Next</div>
            <h2 data-id="element-472">Ready to switch?</h2>
            <p data-id="element-473">Join the teams moving from noise to clarity.</p>
            <Link to="/contact" className="cta" data-id="element-474">
              Contact us <span className="arrow" data-id="element-475">→</span>
            </Link>
          </section>

          <Footer data-id="element-476" />
        </article>

        <pre className="json-view" dangerouslySetInnerHTML={{
        __html: jsonHtml
      }} data-id="element-477" />
      </main>
    </>;
}